import { Bookmark, List as ListIcon, LogOut, ShieldAlert, User as UserIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { Menu, MenuItem } from '@/components/ui/Menu'
import { useCurrentUser, useLogout } from '@/features/auth'
import { useT } from '@/i18n'

interface Props {
  trigger: (props: { open: boolean; toggle: () => void }) => ReactNode
  align?: 'left' | 'right'
  /** 侧栏够宽时这些项已经在导航里了，不必重复 */
  includeNavItems?: boolean
}

/**
 * 账号菜单。桌面侧栏和手机底栏共用。
 *
 * 手机端整个侧栏是隐藏的，如果这里不给入口，个人资料 / 书签 / 列表 / 退出登录
 * 就**一个都够不到**（设置在底栏有独立入口）—— 所以 includeNavItems
 * 在窄屏下必须打开。
 */
export function AccountMenu({ trigger, align = 'left', includeNavItems = false }: Props) {
  const { user } = useCurrentUser()
  const logout = useLogout()
  const navigate = useNavigate()
  const t = useT()

  if (!user) return null

  const go = (close: () => void, to: string) => () => {
    close()
    navigate(to)
  }

  return (
    <Menu align={align} trigger={trigger}>
      {(close) => (
        <>
          {includeNavItems && (
            <>
              <MenuItem onClick={go(close, `/${user.username}`)}>
                <UserIcon className="size-4" /> {t('nav.profile')}
              </MenuItem>
              <MenuItem onClick={go(close, '/bookmarks')}>
                <Bookmark className="size-4" /> {t('nav.bookmarks')}
              </MenuItem>
              <MenuItem onClick={go(close, '/lists')}>
                <ListIcon className="size-4" /> {t('nav.lists')}
              </MenuItem>
              {user.is_admin && (
                <MenuItem onClick={go(close, '/admin')}>
                  <ShieldAlert className="size-4" /> {t('nav.admin')}
                </MenuItem>
              )}
            </>
          )}
          <MenuItem
            onClick={() => {
              close()
              logout.mutate()
            }}
          >
            <LogOut className="size-4" /> {t('account.logout', { username: user.username })}
          </MenuItem>
        </>
      )}
    </Menu>
  )
}
